import type { RoleDefinition } from "../../app/roles";
import { useEffect } from "react";
import { ImpactTrace } from "../../components/ImpactTrace";
import { scenarioEvidence } from "../../data/seed";
import type { TraceNode } from "../../domain";
import { DepthBrief, PerspectiveFieldGuide, defineDepthContent, useDepth, useDepthValue } from "../../presentation";
import { useScenario } from "../../simulation/ScenarioContext";

type InfrastructureTraceProps = {
  role: RoleDefinition;
  onOpenIncident: () => void;
};

const infrastructureDepthContent = defineDepthContent({
  overview: {
    summary: "Follow the service interruption from the campus service to the person experiencing it.",
    title: "Service-to-person story",
    signals: ["One affected academic building", "Phone and workstation service impact", "Assigned user at the end of the path"] as const,
    recordDetail: (node: TraceNode) => node.detail,
  },
  operations: {
    summary: "Traverse physical and logical records, inspect status, and link relevant evidence to INC-2048.",
    title: "Trace and evidence work",
    signals: ["Switch, port, VLAN, and device state", "Selectable evidence records", "Bidirectional operational traversal"] as const,
    recordDetail: (node: TraceNode) => node.operationalDetail,
  },
  architecture: {
    summary: "Inspect the complete typed path, stable identifiers, directionality, and source record for each relationship.",
    title: "Node and edge architecture",
    signals: ["CR-SW-04 and NA-214-A1 anchors", "Physical, logical, asset, and assignment edges", "Seed and simulation provenance"] as const,
    recordDetail: (node: TraceNode) => node.architectureDetail,
  },
});

export function InfrastructureTrace({ role, onOpenIncident }: InfrastructureTraceProps) {
  const { depth } = useDepth();
  const lens = useDepthValue(infrastructureDepthContent);
  const {
    linkedEvidenceIds,
    linkEvidence,
    selectedNode,
    selectTraceNode,
    stage,
    startScenario,
  } = useScenario();
  const nodeEvidence = scenarioEvidence.find((item) => item.traceNodeId === selectedNode.id);

  useEffect(() => {
    selectTraceNode(depth === "overview" ? "svc-staff-wifi" : depth === "operations" ? "CR-SW-04" : "NA-214-A1");
  }, [depth]);

  return (
    <section aria-labelledby="trace-title">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Infrastructure source of truth</p>
          <h1 id="trace-title">North Academic impact trace</h1>
          <p>{lens.summary}</p>
        </div>
        {stage === "calm" ? (
          <button className="button button--primary" onClick={() => startScenario(role.label)} type="button">
            Start scenario
          </button>
        ) : (
          <button className="button button--secondary" onClick={onOpenIncident} type="button">
            Return to INC-2048
          </button>
        )}
      </div>

      <DepthBrief summary={lens.summary} signals={lens.signals} title={lens.title} />
      <PerspectiveFieldGuide surface="infrastructure" />

      {depth === "overview" && (
        <section className="depth-surface overview-action-grid" aria-label="Service impact story">
          <article className="overview-action-card"><span>Service</span><strong>Staff Wireless + Campus Voice</strong><p>Both services share the same affected building boundary.</p></article>
          <article className="overview-action-card"><span>Place and device</span><strong>North Academic · TEL-0318</strong><p>The desk phone remains powered but cannot register reliably.</p></article>
          <article className="overview-action-card"><span>Person outcome</span><strong>Priya Nair has a workaround</strong><p>Teams calling remains the documented temporary option.</p></article>
        </section>
      )}

      {depth === "architecture" && (
        <section className="depth-surface architecture-record-grid" aria-label="Topology edge types">
          <article className="architecture-record-card"><span>Logical edges</span><strong>service → VLAN → device</strong><p>Membership is explicit and never inferred from display labels or IP strings.</p></article>
          <article className="architecture-record-card"><span>Physical edges</span><strong>interface → panel → jack</strong><p>Termination records make every physical hop traversable in either direction.</p></article>
          <article className="architecture-record-card"><span>Ownership edges</span><strong>asset → assignment → person</strong><p>Separate auditable records calculate the human impact boundary.</p></article>
        </section>
      )}

      <article className="panel trace-workspace">
        <header className="panel__header">
          <div>
            <p className="eyebrow">Bidirectional relationship path</p>
            <h2>Service → switch → room → phone → person</h2>
          </div>
          <span>{stage === "calm" ? "Baseline view" : "Incident highlighting active"}</span>
        </header>
        <ImpactTrace />
      </article>

      <div className="trace-detail-grid">
        <article className="panel selected-record">
          <header className="panel__header">
            <div><p className="eyebrow">{selectedNode.eyebrow}</p><h2>{selectedNode.label}</h2></div>
            {depth !== "overview" && <span className="mono">{selectedNode.id}</span>}
          </header>
          <p>{lens.recordDetail(selectedNode)}</p>
          <dl>
            {depth === "overview" && (
              <>
                <div><dt>Current state</dt><dd>{selectedNode.status}</dd></div>
                <div><dt>Why it matters</dt><dd>{selectedNode.detail}</dd></div>
              </>
            )}
            {depth === "operations" && (
              <>
                <div><dt>Status</dt><dd>{selectedNode.status}</dd></div>
                <div><dt>Evidence source</dt><dd>{selectedNode.evidence}</dd></div>
                <div><dt>Working detail</dt><dd>{selectedNode.operationalDetail}</dd></div>
              </>
            )}
            {depth === "architecture" && (
              <>
                <div><dt>Stable identifier</dt><dd>{selectedNode.id}</dd></div>
                <div><dt>Record type</dt><dd>{selectedNode.kind}</dd></div>
                <div><dt>Evidence source</dt><dd>{selectedNode.evidence}</dd></div>
                <div><dt>Relationship model</dt><dd>{selectedNode.architectureDetail}</dd></div>
              </>
            )}
          </dl>
          {depth !== "overview" && <div className="record-shortcuts">
            <span>Anchor records</span>
            {["CR-SW-04", "NA-214-A1", "TEL-0318", "USR-PRIYA-NAIR"].map((id) => (
              <button key={id} onClick={() => selectTraceNode(id)} type="button">{id}</button>
            ))}
          </div>}
        </article>

        <article className="panel">
          <header className="panel__header">
            <div><p className="eyebrow">Evidence action</p><h2>Current record</h2></div>
          </header>
          {nodeEvidence ? (
            <div className="trace-evidence-action">
              <strong>{nodeEvidence.label}</strong>
              <span>{nodeEvidence.source} · {nodeEvidence.confidence} confidence</span>
              <p>{nodeEvidence.supports}</p>
              <button
                className="button button--primary"
                disabled={linkedEvidenceIds.includes(nodeEvidence.id) || stage === "calm"}
                onClick={() => linkEvidence(nodeEvidence.id, role.label)}
                type="button"
              >
                {linkedEvidenceIds.includes(nodeEvidence.id)
                  ? "Linked to INC-2048"
                  : stage === "calm"
                    ? "Start scenario to link"
                    : "Add evidence to incident"}
              </button>
            </div>
          ) : (
            <p className="empty-copy">
              This relationship is verified but does not have a standalone evidence object. Select CR-SW-04, Gi1/0/18,
              or TEL-0318.
            </p>
          )}
        </article>
      </div>
    </section>
  );
}
